import { enrollmentKeys } from '@/hooks/use-enrollments';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import {
  CreateRecurrenceDto,
  RecurrencePlan,
  RecurrenceSummary,
} from '@/types/recurrence';
import { USER_ROLE } from '@/types/user-role';
import { isProviderRole } from '@/utils/user-role';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const recurrenceKeys = {
  all: ['recurrences'] as const,
  mine: () => [...recurrenceKeys.all, 'mine'] as const,
  pending: () => [...recurrenceKeys.all, 'pending'] as const,
};

/** Cliente adota a recorrência de um serviço (modo AUTO). */
export function useCreateRecurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateRecurrenceDto) => {
      const res = await api.post<RecurrenceSummary>(
        '/client/schedule/recurrences',
        dto,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurrenceKeys.mine() });
      // "Meus Contratos" lê de outro cache (enrollmentKeys); sem isso o
      // pedido pendente recém-criado não aparece lá até o cache expirar.
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

/** Pré-visualiza a agenda recorrente montada pelo cliente (não persiste). */
export function usePreviewRecurrence() {
  return useMutation({
    mutationFn: async (dto: CreateRecurrenceDto) => {
      const res = await api.post<RecurrencePlan>(
        '/client/schedule/recurrences/preview',
        dto,
      );
      return res.data;
    },
  });
}

/**
 * Prévia da grade fixa montada pelo prestador ao criar/editar um contrato:
 * sessões geradas, datas já ocupadas e validade. Não persiste.
 */
export function useProviderPreviewRecurrence() {
  return useMutation({
    mutationFn: async (dto: CreateRecurrenceDto) => {
      const res = await api.post<RecurrencePlan>(
        '/provider/me/schedule/recurrences/preview',
        dto,
      );
      return res.data;
    },
  });
}

/** Minhas recorrências (cliente). */
export function useMyRecurrences() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isClient = user?.role === USER_ROLE.CLIENT;
  return useQuery({
    queryKey: recurrenceKeys.mine(),
    queryFn: async () => {
      const res = await api.get<RecurrenceSummary[]>(
        '/client/schedule/recurrences',
      );
      return res.data;
    },
    enabled: isHydrated && isClient,
  });
}

/** Recorrências aguardando aprovação do prestador. */
export function useProviderPendingRecurrences() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isProvider = isProviderRole(user?.role);
  return useQuery({
    queryKey: recurrenceKeys.pending(),
    queryFn: async () => {
      const res = await api.get<RecurrenceSummary[]>(
        '/provider/me/schedule/recurrences/pending',
      );
      return res.data;
    },
    enabled: isHydrated && isProvider,
  });
}

export function useApproveRecurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enrollmentId: string) => {
      const res = await api.post<RecurrenceSummary>(
        `/provider/me/schedule/recurrences/${enrollmentId}/approve`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurrenceKeys.pending() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

export function useRejectRecurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      reason,
    }: {
      enrollmentId: string;
      reason?: string;
    }) => {
      const res = await api.post<RecurrenceSummary>(
        `/provider/me/schedule/recurrences/${enrollmentId}/reject`,
        { reason },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurrenceKeys.pending() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}
